import { useState, useEffect  } from 'react';
import { 
  Box,
  Typography,
  Button,
  Card,
  TextField,
  Stack,
  Chip,
  IconButton,
  Skeleton,
  Container,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useNavigate } from 'react-router-dom';
import { getCouponTemplateList, userCouponlist } from '../api/modules/couponTemplate';
import SearchIcon from '@mui/icons-material/Search';
import { showMessage } from '../utils';
import { removeToken } from '../utils/auth'
import { isInApp } from '../utils/environment';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import UserMenu from '../components/UserMenu';
import TopBar from '../components/TopBar';
import { handleCopyLink } from '../utils/share';

// 自定义样式组件
const TabButton = styled(Button)(({ theme, active }) => ({
  flex: 1,
  height: '36px',
  backgroundColor: active === 'true' ? '#FFFFFF !important' : '#F3F4F6 !important',
  color: active === 'true' ? '#1A1A1A' : '#64748B',
  textTransform: 'none',
  fontSize: '15px',
  fontWeight: active === 'true' ? 600 : 500,
  borderRadius: '7px',
}));

const SearchBar = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#FFF',
    '& fieldset': {
      borderColor: '#E5E7EB',
    },
    '& input': {
      padding: '12px 16px 12px 44px',
      fontSize: '15px',
      color: '#6B7280',
      '&::placeholder': {
        color: '#9CA3AF',
      }
    }
  }
});

const FilterChip = styled(Chip)(({ theme, active }) => ({
  borderRadius: '26px',
  backgroundColor: active ? '#000' : '#F3F4F6',
  color: active ? '#fff' : '#6B7280',
  padding: '4px 12px',
  height: '32px',
  fontSize: '14px',
  fontWeight: 500,
}));

export default function TaxDeclaration() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('referral');
  const [activeFilter, setActiveFilter] = useState('All');
  const [couponTemplateList, setCouponTemplateList] = useState([]);
  const [userCoupons, setUserCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUserCoupons, setIsLoadingUserCoupons] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  // 保留原有的优惠券模板列表获取
  useEffect(() => {
    getCouponTemplateList((rst) => {
      setIsLoading(false);
      const { code, data, msg } = rst;
      if (code == 200) {
        setCouponTemplateList(data);
      } else {
        showMessage(msg, 'error');
      }
    });
  }, []);

  // 获取用户优惠券列表
  const fetchUserCoupons = async (status = 0) => {
    setIsLoadingUserCoupons(true);
    try {
      const params = {
        pageSize: 100,
        pageNum: 1,
        ...(searchText && { name: searchText }),
        ...(status !== 0 && { status })
      };
      
      await userCouponlist(params, (result) => {
        const { code, rows, msg } = result;
        if (code === 200 && rows) {
          setUserCoupons(rows);
        } else {
          showMessage(msg, 'error');
        }
      });
    } catch (error) {
      showMessage(error.message, 'error');
    } finally {
      setIsLoadingUserCoupons(false);
    }
  };

  // Tab切换处理
  useEffect(() => {
    if (activeTab === 'myReward') {
      fetchUserCoupons(getStatusCode(activeFilter));
    }
  }, [activeTab]);

  // 加载状态的骨架屏组件
  const LoadingSkeleton = () => (
    <Card 
      sx={{ 
        mx: 2,
        p: 3, 
        mb: 2, 
        borderRadius: '16px',
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        bgcolor: '#FFF',
        minHeight: '280px'
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Skeleton width={120} height={32} />
          <Skeleton width={80} height={24} />
        </Stack>
        <Skeleton width={140} height={60} />
        <Skeleton width={100} height={24} />
        <Stack spacing={2}>
          <Skeleton width="100%" height={20} />
          <Skeleton width="100%" height={20} />
          <Skeleton width="100%" height={20} />
        </Stack>
        <Skeleton width="100%" height={48} sx={{ borderRadius: '100px' }} />
      </Stack>
    </Card>
  );

  // 空状态组件
  const EmptyState = () => (
    <Card 
      sx={{ 
        mx: 2,
        p: 3, 
        mb: 2, 
        borderRadius: '16px',
        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        bgcolor: '#FFF',
        minHeight: '280px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <InboxOutlinedIcon 
        sx={{ 
          width: 64, 
          height: 64, 
          mb: 2,
          color: '#9CA3AF'
        }} 
      />
      <Typography 
        sx={{ 
          color: '#6B7280',
          fontSize: '15px',
          fontWeight: 500
        }}
      >
        List is empty
      </Typography>
    </Card>
  );

  // 根据筛选条件获取状态码
  const getStatusCode = (filter) => {
    switch (filter) {
      case 'Active':
        return 1; // 未使用
      case 'Used':
        return 2; // 已使用
      case 'Expired':
        return 3; // 已过期
      case 'All':
      default:
        return 0; // 全部
    }
  };

  // 根据状态码获取显示状态
  const getStatusLabel = (status) => {
    switch (status) {
      case 1:
        return 'Best Value'; // 未使用显示为 Best Value
      case 2:
        return 'Used';
      case 3:
        return 'Expired';
      case 4:
        return 'Frozen';
      default:
        return 'Best Value';
    }
  };

  // 处理筛选变化
  const handleFilterChange = (newFilter) => {
    setActiveFilter(newFilter);
    fetchUserCoupons(getStatusCode(newFilter));
  };

  // 处理搜索
  const handleSearch = (event) => {
    setSearchText(event.target.value);
    fetchUserCoupons(getStatusCode(activeFilter));
  };

  // 添加跳转到详情页的处理函数
  const handleViewDetails = (coupon) => {
    navigate('/campaigns/invitations/reward-details', { 
      state: { 
        couponData: {
          ...coupon,
          purchaseDate: new Date().toLocaleDateString(), // 示例数据
          purchasePrice: 49.99,
          savings: 50.01
        }
      }
    });
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };


  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', paddingBottom: 2 }}>
      {/* Logo和登录按钮区域 - PC端和移动端都显示 */}
      <TopBar
        isLogin={true}
        onShareClick={() => navigate('/campaigns/invitations/referral')}
        onCouponClick={() => navigate('/campaigns/invitations/tax-declaration')}
        handleCopyLink={handleCopyLink}
        onAvatarClick={handleUserMenuOpen}
        userMenuAnchor={userMenuAnchor}
        onUserMenuClose={handleUserMenuClose}
        navigate={navigate}
        onLogout={() => {
          removeToken();
          showMessage("Logout successfully", "success");
          setTimeout(() => {
            navigate("/login");
          }, 1500);
          handleUserMenuClose();
        }}
      />
      {/* Header */}
      <Box
          sx={{
            mt: { xs: '56px', md: '64px' },
            width: '100%',
            // background: '#f6efe7',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // background: '#18155a',
          }}
        >
          {/* 比例容器，保持 1200x628 的比例 */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '1200px',
              mx: 'auto',
              '&:before': {
                content: '""',
                display: 'block',
                paddingTop: '66.6666%', // 1536:1024  1024/1536 = 66.6666%
              },
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/banner-3.jpg`}
              alt="Header background"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover', // 如果你想完整展示图片用 contain
              }}
            />
          </Box>
        </Box>

      {/* 主要内容区域 */}
      <Container sx={{ 
        maxWidth: '1200px !important',
        px: { xs: 0, md: 3 },
        mx: 'auto',
        position: 'relative',
        zIndex: 1,
        bgcolor: '#FFF',
        borderRadius: '8px',
        pt: 3
      }}>
        <Typography sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#000',
          mb: 2,
          textAlign: 'center'
        }}>
          Rewards   
        </Typography>

        <Box 
          sx={{ 
            mx: 2,
            mb: 3, 
            p: 0.5,
            bgcolor: '#F3F4F6',
            borderRadius: '9px',
            display: 'flex',
            gap: 0.5
          }}
        >
          <TabButton 
            active={activeTab === 'referral' ? 'true' : 'false'}
            onClick={() => setActiveTab('referral')}
          >
            Referral Reward
          </TabButton>
          <TabButton
            active={activeTab === 'myReward' ? 'true' : 'false'} 
            onClick={() => setActiveTab('myReward')}
          >
            My Reward
          </TabButton>
        </Box>

        {activeTab === 'referral' ? (
          <>
            {isLoading ? (
              <LoadingSkeleton />
            ) : couponTemplateList.length > 0 ? (
              couponTemplateList.map((item, index) => (
                <Card 
                  key={index}
                  sx={{ 
                    mx: 2,
                    mb: 4,
                    borderRadius: '8px',
                    boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)',
                    bgcolor: 'white',
                    overflow: 'hidden',
                    p: 0
                  }}
                >
                  {/* 卡片头部 */}
                  <Box
                    sx={{
                      background: 'linear-gradient(90deg, rgb(143, 34, 46) 0%, rgb(22, 119, 255) 100%)',
                      p: 3,
                      color: 'white',
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 22 }}>
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontWeight: 400, fontSize: 15, opacity: 0.85 }}>
                        {item.desc || 'Perfect for personal tax filing'}
                      </Typography>
                    </Box>
                    <Chip 
                      label={item.type === 1 ? 'Business' : 'Individual'} 
                      sx={{ 
                        bgcolor: '#fff',
                        color: '#7f5fff',
                        borderRadius: '8px',
                        height: '28px',
                        fontSize: '13px',
                        fontWeight: 700
                      }} 
                    />
                  </Box>

                  {/* 价格区块 */}
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                      <Typography sx={{ color: '#b0b0b0', fontSize: 22, textDecoration: 'line-through', mr: 1 }}>
                        ${item.facePrice}
                      </Typography>
                      <Box sx={{ bgcolor: '#ffeaea', color: '#ff4d4f', borderRadius: 2, px: 1.5, fontSize: 13, fontWeight: 600 }}>
                        Original Price
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 40, color: '#7f5fff', mb: 1 }}>
                      ${item.actualPrice}
                    </Typography>
                    <Box sx={{ bgcolor: '#d1fae5', color: '#059669', borderRadius: 8, px: 2, py: 0.5, display: 'inline-block', fontWeight: 700, fontSize: 16, mb: 2 }}>
                      Save ${(item.facePrice - item.actualPrice).toFixed(2)}
                    </Box>
                  </Box>

                  {/* 优惠券详情 */}
                  <Box sx={{ px: 3, pb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon sx={{ mr: 1, fontSize: 18, color: '#7f5fff' }} />
                      <Typography sx={{ fontWeight: 500, fontSize: 15 }}>
                        Minimum Purchase: <b>${item.facePrice}</b>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccessTimeIcon sx={{ mr: 1, fontSize: 18, color: '#7f5fff' }} />
                      <Typography sx={{ fontWeight: 500, fontSize: 15 }}>
                        Validity: <b>{item.num} {item.unit}</b>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <WarningIcon sx={{ mr: 1, fontSize: 18, color: '#7f5fff' }} />
                      <Typography sx={{ fontWeight: 500, fontSize: 15 }}>
                        Policy: <b>Non-refundable</b>
                      </Typography>
                    </Box>
                    <Button 
                      variant="contained"
                      fullWidth
                      onClick={() => navigate('/campaigns/invitations/pay', { state: { couponTemplateListItem: item } })}
                      sx={{ 
                        borderRadius: '12px',
                        background: 'linear-gradient(90deg, #7f5fff 0%, #007bff 100%)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: 18,
                        py: 1.5,
                        boxShadow: 'none',
                        textTransform: 'none',
                        mt: 2,
                        '&:hover': {
                          background: 'linear-gradient(90deg, rgb(143, 34, 46) 0%, rgb(22, 119, 255) 100%)',
                          boxShadow: '0 2px 8px #7f5fff33'
                        }
                      }}
                    >
                      Buy Now
                    </Button>
                  </Box>
                </Card>
              ))
            ) : (
              <EmptyState />
            )}

            <Box sx={{ px: 2, mt: 4, pd: 2 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  fontSize: '16px',
                  fontWeight: 900,
                  color: '#000',
                  fontFamily: 'Roboto'
                }}
              >
                Frequently Asked Questions
              </Typography>
              <Stack spacing={3}>
                <Box>
                  <Typography sx={{ 
                    fontSize: '14px', 
                    fontWeight: 500, 
                    color: '#000',
                    mb: 1,
                    fontFamily: 'Roboto'
                  }}>
                    How do I use my coupon?
                  </Typography>
                  <Typography sx={{ 
                    color: '#6B7280',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    fontFamily: 'Roboto',
                    fontWeight: 400
                  }}>
                    Simply present your coupon code at checkout when making a purchase.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ 
                    fontSize: '14px', 
                    fontWeight: 500,
                    color: '#000',
                    mb: 1,
                    fontFamily: 'Roboto'
                  }}>
                    Are coupons refundable?
                  </Typography>
                  <Typography sx={{ 
                    color: '#6B7280',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    fontFamily: 'Roboto',
                    fontWeight: 400
                  }}>
                    No, all coupons are non-refundable once purchased.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ 
                    fontSize: '14px', 
                    fontWeight: 500,
                    color: '#000',
                    mb: 1,
                    fontFamily: 'Roboto'
                  }}>
                    What's the expiration policy?
                  </Typography>
                  <Typography sx={{ 
                    color: '#6B7280',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    fontFamily: 'Roboto',
                    fontWeight: 400
                  }}>
                    Coupons are valid for 90 days from the date of purchase.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </>
        ) : (
          <Box sx={{ px: 2 }}>
            <Box sx={{ position: 'relative', mb: 2 }}>
              <SearchIcon sx={{ 
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9CA3AF',
                fontSize: 20
              }} />
              <SearchBar
                fullWidth
                placeholder="Search coupons..."
                value={searchText}
                onChange={handleSearch}
              />
            </Box>

            <Stack 
              direction="row" 
              spacing={1} 
              sx={{ 
                mb: 3,
                '& .MuiChip-root': {
                  cursor: 'pointer'
                }
              }}
            >
              {['All', 'Active', 'Used', 'Expired'].map((filter) => (
                <FilterChip 
                  key={filter}
                  label={filter}
                  active={filter === activeFilter}
                  onClick={() => handleFilterChange(filter)}
                />
              ))}
            </Stack>

            {isLoadingUserCoupons ? (
              <LoadingSkeleton />
            ) : userCoupons.length > 0 ? (
              userCoupons.map((coupon) => (
                <Card 
                  key={coupon.couponId} 
                  sx={{ 
                    mb: 2, 
                    p: 3,
                    borderRadius: '12px',
                    background: '#FFFFFF',
                    border: '1px solid #0000001A',
                    boxShadow: 'none'
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontSize: '18px',
                          fontWeight: 900,
                          color: '#000',
                          mb: 1,
                          fontFamily: 'Roboto'
                        }}
                      >
                        {coupon.name}
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontSize: '32px',
                          fontWeight: 700,
                          color: '#000',
                          mb: 0.5,
                          fontFamily: 'Roboto'
                        }}
                      >
                        ${coupon.facePrice}
                      </Typography>
                      <Typography 
                        sx={{ 
                          color: '#6B7280',
                          fontSize: '18px',
                          mb: 2,
                          fontFamily: 'Roboto',
                          fontWeight: 400
                        }}
                      >
                        {coupon.code}
                      </Typography>
                    </Box>
                    <Chip 
                      label={getStatusLabel(coupon.status)}
                      sx={{
                        bgcolor: coupon.status === 1 ? '#007AFF' : '#E5E7EB',
                        color: coupon.status === 1 ? '#fff' : '#6B7280',
                        borderRadius: '8px',
                        height: '28px',
                        fontSize: '13px',
                        fontWeight: 500
                      }}
                    />
                  </Stack>

                  <Stack spacing={2} sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: '#6B7280', fontSize: '14px', fontFamily: 'Roboto', fontWeight: 400 }}>
                        Valid Until
                      </Typography>
                      <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 500, fontFamily: 'Roboto' }}>
                        {new Date(coupon.validEndTime).toLocaleDateString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: '#6B7280', fontSize: '14px', fontFamily: 'Roboto', fontWeight: 400 }}>
                        Minimum Purchase
                      </Typography>
                      <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 500, fontFamily: 'Roboto' }}>
                        ${coupon.actualPrice}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ color: '#6B7280', fontSize: '14px', fontFamily: 'Roboto', fontWeight: 400 }}>
                        Usage Limit
                      </Typography>
                      <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 500, fontFamily: 'Roboto' }}>
                        One-time use
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      fullWidth
                      onClick={() => {
                        navigator?.clipboard?.writeText(coupon.code);
                        showMessage('Code copied successfully!', 'success');
                      }}
                      sx={{
                        color: '#000',
                        borderRadius: '8px',
                        textTransform: 'none',
                        py: 1.5,
                        fontSize: '14px',
                        fontWeight: 500,
                        border: '1px solid #E5E7EB',
                        '&:hover': {
                          backgroundColor: '#E5E7EB', // 修改 hover 背景色
                          // borderColor: '#007AFF', // 可选：修改 hover 边框颜色
                        },
                      }}
                    >
                      Copy Code
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<InfoIcon />}
                      fullWidth
                      onClick={() => handleViewDetails(coupon)}
                      sx={{
                        color: '#000',
                        borderRadius: '8px',
                        textTransform: 'none',
                        py: 1.5,
                        fontSize: '14px',
                        fontWeight: 500,
                        border: '1px solid #E5E7EB',
                        '&:hover': {
                          backgroundColor: '#E5E7EB', // 修改 hover 背景色
                          // borderColor: '#007AFF', // 可选：修改 hover 边框颜色
                        },
                      }}
                    >
                      Details
                    </Button>
                  </Stack>
                </Card>
              ))
            ) : (
              <EmptyState />
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}
